import React, { createContext, useContext, useState, useMemo, ReactNode, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_CURRENCIES, CurrencyInfo, getConvertedPrices } from '../lib/currency';
import { SUPPORTED_LANGUAGES, resolveSupportedLanguage } from '../i18n';
import { useNotificationContext } from './NotificationContext';
import { logger } from '../lib/logger';

interface CurrencyContextType {
  currency: string;
  currencyInfo: CurrencyInfo;
  changeCurrency: (newCurrency: string) => void;
  prices: Record<string, { original: number; converted: number; formatted: string }>;
  supportedCurrencies: Record<string, CurrencyInfo>;
  isAutoLinked: boolean;
  enableAutoLink: () => void;
  disableAutoLink: () => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

/** Devise catalogue associée à une langue (clés canoniques i18n). */
export function getCurrencyForLanguage(langCode: string | undefined | null): string {
  const resolved = resolveSupportedLanguage(langCode);
  if (resolved) {
    const currency = SUPPORTED_LANGUAGES[resolved]?.currency;
    if (currency && SUPPORTED_CURRENCIES[currency]) {
      return currency;
    }
  }
  return 'GBP';
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const { addNotification } = useNotificationContext();
  
  // Liaison langue ↔ devise ON par défaut (et à chaque refresh de page)
  const [isAutoLinked, setIsAutoLinked] = useState<boolean>(true);
  
  const [currency, setCurrency] = useState<string>(() => {
    // Auto-link : la langue URL prime toujours sur preferred-currency (sinon GBP figé)
    if (typeof window !== 'undefined') {
      const pathLang = window.location.pathname.match(
        /^\/([a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?:\/|$)/i
      )?.[1];
      return getCurrencyForLanguage(pathLang || i18n.language || 'en');
    }
    return getCurrencyForLanguage(i18n.language || 'en');
  });
  
  // Suivre la langue tant que la liaison auto est active
  useEffect(() => {
    if (!isAutoLinked) return;

    const expectedCurrency = getCurrencyForLanguage(i18n.language);
    logger.debug(
      `Auto-link currency: lang=${i18n.language} → ${expectedCurrency} (was ${currency})`
    );

    if (expectedCurrency !== currency && SUPPORTED_CURRENCIES[expectedCurrency]) {
      setCurrency(expectedCurrency);
      // Ne pas laisser une vieille préférence écraser la langue au prochain mount
      localStorage.removeItem('preferred-currency');
    }
  }, [i18n.language, isAutoLinked, currency, addNotification]);
  
  const changeCurrency = useCallback((newCurrency: string) => {
    if (SUPPORTED_CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('preferred-currency', newCurrency);
      
      // Désactiver la liaison auto pour cette session uniquement
      setIsAutoLinked(false);
    }
  }, []);
  
  const enableAutoLink = useCallback(() => {
    setIsAutoLinked(true);
    const expectedCurrency = getCurrencyForLanguage(i18n.language);
    if (SUPPORTED_CURRENCIES[expectedCurrency]) {
      setCurrency(expectedCurrency);
      localStorage.removeItem('preferred-currency');
    }
  }, [i18n.language]);
  
  const disableAutoLink = useCallback(() => {
    setIsAutoLinked(false);
    localStorage.setItem('preferred-currency', currency);
  }, [currency]);
  
  const prices = useMemo(() => getConvertedPrices(currency), [currency]);
  
  const value = useMemo(() => ({
    currency,
    currencyInfo: SUPPORTED_CURRENCIES[currency] || SUPPORTED_CURRENCIES.GBP,
    changeCurrency,
    prices,
    supportedCurrencies: SUPPORTED_CURRENCIES,
    isAutoLinked,
    enableAutoLink,
    disableAutoLink,
  }), [currency, prices, isAutoLinked, changeCurrency, enableAutoLink, disableAutoLink]);
  
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
